import { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import PHOTO_URL from "./jefferson-photo.jpeg";
import { sunIcon, moonIcon } from "./darkmode";
 

// ═══════════════════════════════════════════════════════
// THEMES
// ═══════════════════════════════════════════════════════
const themes = {
  light: {
    bg: "#f8fafc", card: "#ffffff", cardHov: "#f1f5f9",
    pri: "#0369a1", priDk: "#075985", priSoft: "rgba(3,105,161,.08)", priBrd: "rgba(3,105,161,.18)",
    acc: "#4f46e5", accSoft: "rgba(79,70,229,.07)", accBrd: "rgba(79,70,229,.15)",
    text: "#0f172a", sec: "#475569", mut: "#94a3b8",
    navBg: "rgba(248,250,252,.88)", navBrd: "rgba(0,0,0,.06)",
    toggleBrd: "rgba(0,0,0,.12)", toggleInact: "#94a3b8",
    grad1: "rgba(3,105,161,.04)", grad2: "rgba(79,70,229,.03)",
    sectBg1: "rgba(3,105,161,.02)", sectBg2: "rgba(79,70,229,.02)",
    chipHov: "rgba(3,105,161,.22)", shadow: "0 1px 3px rgba(0,0,0,.06)",
    photoShadow: "0 8px 40px rgba(0,0,0,.12)", quoteBg: "rgba(79,70,229,.04)",
  },
  dark: {
    bg: "#0a0e14", card: "#111827", cardHov: "#1a2234",
    pri: "#38bdf8", priDk: "#0ea5e9", priSoft: "rgba(56,189,248,.08)", priBrd: "rgba(56,189,248,.14)",
    acc: "#818cf8", accSoft: "rgba(129,140,248,.07)", accBrd: "rgba(129,140,248,.14)",
    text: "#f0f4f8", sec: "#8b9bb4", mut: "#4b5c74",
    navBg: "rgba(10,14,20,.88)", navBrd: "rgba(56,189,248,.1)",
    toggleBrd: "rgba(255,255,255,.1)", toggleInact: "#4b5c74",
    grad1: "rgba(56,189,248,.06)", grad2: "rgba(129,140,248,.04)",
    sectBg1: "rgba(56,189,248,.012)", sectBg2: "rgba(129,140,248,.012)",
    chipHov: "rgba(56,189,248,.25)", shadow: "0 2px 8px rgba(0,0,0,.3)",
    photoShadow: "0 8px 40px rgba(0,0,0,.5)", quoteBg: "rgba(129,140,248,.06)",
  },
};

const ThemeCtx = createContext(themes.light);
function useTheme() { return useContext(ThemeCtx); }

// ═══════════════════════════════════════════════════════
// i18n
// ═══════════════════════════════════════════════════════
const dict = {
  pt: {
    navItems: ["Sobre", "Expertise", "Telerik", "Source Genesys", "Contato"],
    navKeys: ["about", "expertise", "telerik", "sg", "contact"],
    hero: {
      badge: "Progress Telerik Champion · 4x C# Corner MVP",
      name: "Jefferson S. Motta",
      role: "Engenheiro de Software",
      sub: "Fundador da Menphis Sistemas Inteligentes e da Source Genesys Tecnologies AI. Consultor principal e reseller Progress Telerik no Brasil. Mais de 29 anos criando sistemas que resolvem problemas reais.",
      cta1: "Currículo PDF", cta2: "Contato",
    },
    stats: [
      { v: "29+", l: "Anos de Experiência" }, { v: "900K+", l: "Views C# Corner" },
      { v: "4x", l: "C# Corner MVP" }, { v: "10+", l: "Sistemas em Produção" },
    ],
    about: {
      label: "SOBRE", title: "Engenharia com visão humana",
      blocks: [
        "Comecei em 1996 e desde então não parei. Criei componentes em VB6, migrei para .NET na primeira versão, e hoje projeto plataformas SaaS multi-tenant com .NET 10, React 19 e Next.js. Já otimizei queries em bases com mais de 10 milhões de registros e liderei equipes multifuncionais em empresas de diversos portes.",
        "Em 2024 (e 2025) recebi o título de Progress Telerik Champion — o primeiro da América Latina. Sou reseller oficial e consultor principal da Telerik no Brasil, autor no blog oficial da Progress e responsável pelo primeiro webinar em português sobre a plataforma.",
        "Atualmente curso pós-graduação em Neurociência, Desenvolvimento Humano e Mediação. Entender como o cérebro processa informação me ajuda a projetar interfaces mais intuitivas. Tecnologia sem empatia é só código.",
      ],
      quote: "\"Tecnologia sem empatia é só código.\"", quoteBy: "— Jefferson S. Motta",
    },
    expertise: {
      label: "EXPERTISE", title: "Stack & Competências",
      cards: [
        { i: "⚙️", t: "Backend .NET", d: "C#, .NET 10, ASP.NET Core, Web API, ORM próprio (sem EF Core / Dapper), xUnit, Windows Services." },
        { i: "⚛️", t: "Frontend Moderno", d: "React 19, Next.js, TypeScript, Redux, Angular, Blazor. Telerik Kendo UI para componentes enterprise." },
        { i: "🗄️", t: "Bancos de Dados", d: "SQL Server, PostgreSQL, MySQL. T-SQL avançado, tuning, migração e arquitetura multi-tenant." },
        { i: "📈", t: "Observabilidade", d: "Prometheus, Grafana, Loki, Tempo, OpenTelemetry. Métricas P50/P95/P99, Health Checks, SLOs." },
        { i: "🛡️", t: "Resiliência", d: "Polly — Circuit Breaker, Retry, Bulkhead, Rate Limiting, Geo-Blocking. Zero downtime." },
        { i: "☁️", t: "DevOps & Cloud", d: "Azure, Azure DevOps, GitHub Actions, IIS, Windows Server, SonarCloud." },
      ],
    },
    telerik: {
      label: "PROGRESS TELERIK", title: "Champion & Reseller Oficial",
      p1: "Utilizo a plataforma Telerik desde 2014 em todos os meus projetos. São mais de 1.250 componentes UI para .NET e JavaScript que permitem entregar interfaces profissionais em uma fração do tempo.",
      p2: "A NASA utiliza Telerik DevCraft para organizar dados das missões a Marte. Microsoft, IBM, Samsung e mais de 450 empresas da Fortune 500 confiam na mesma tecnologia.",
      items: ["Primeiro Telerik Champion da América Latina", "Autor no blog oficial da Progress", "Primeiro webinar em português sobre Telerik", "Treinamento presencial com certificação", "Reseller e consultor principal no Brasil"],
    },
    sg: {
      label: "SOURCE GENESYS", title: "Gerador de Plataformas SaaS",
      p1: "Minha maior criação. O Source Genesys gera plataformas SaaS completas e prontas para produção a partir da definição do banco de dados. O código-fonte gerado é 100% do cliente — sem vendor lock-in.",
      p2: "Stack: .NET 10 no backend, React + Next.js + Telerik Kendo UI no frontend, multi-tenant nativo com isolamento por schema, observabilidade enterprise (Prometheus, Grafana, Loki, Tempo), resiliência via Polly e testes automatizados com xUnit e Jest.",
      features: ["APIs RESTful geradas automaticamente", "Multi-tenant com isolamento por URI/schema", "Frontend React + Next.js + Kendo UI", "Observabilidade enterprise integrada", "Migração de legados: VB6, Delphi, WebForms", "ORM próprio — controle total"],
      cta: "sourcegenesys.com.br",
    },
    writing: {
      label: "PUBLICAÇÕES", title: "Artigos & Livro",
      items: [
        { emoji: "📝", t: "Blog Progress Telerik", d: "Artigos regulares sobre .NET, React, IA, arquitetura de software e boas práticas de desenvolvimento.", href: "https://www.telerik.com/blogs/author/jefferson-s-motta" },
        { emoji: "🏆", t: "C# Corner", d: "Mais de 900 mil visualizações e 4 prêmios MVP. Conteúdo técnico para a comunidade global .NET.", href: "https://www.c-sharpcorner.com/members/jefferson-s.-motta" },
        { emoji: "📚", t: "Livro na Amazon", d: "Autor publicado — livro disponível na Amazon sobre desenvolvimento de software.", href: "https://www.amazon.com/dp/B0FCBY14DJ/" },
      ],
    },
    contact: { label: "CONTATO", title: "Vamos conversar?", sub: "Precisa de consultoria Telerik, quer criar uma plataforma SaaS com o Source Genesys ou migrar um sistema legado? Entre em contato." },
    footer: `© 1996-${new Date().getFullYear()} Jefferson S. Motta`,
  },
  en: {
    navItems: ["About", "Expertise", "Telerik", "Source Genesys", "Contact"],
    navKeys: ["about", "expertise", "telerik", "sg", "contact"],
    hero: {
      badge: "Progress Telerik Champion · 4x C# Corner MVP",
      name: "Jefferson S. Motta", role: "Software Engineer",
      sub: "Founder of Menphis Sistemas Inteligentes and Source Genesys Tecnologies AI. Principal consultant and Progress Telerik reseller in Brazil. 29+ years turning complex problems into smart systems.",
      cta1: "Download CV", cta2: "Contact",
    },
    stats: [
      { v: "29+", l: "Years of Experience" }, { v: "900K+", l: "C# Corner Views" },
      { v: "4x", l: "C# Corner MVP" }, { v: "10+", l: "Systems in Production" },
    ],
    about: {
      label: "ABOUT", title: "Engineering with a human perspective",
      blocks: [
        "I started in 1996 and never stopped. Built VB6 components, adopted .NET from v1, and today I design complete multi-tenant SaaS platforms with .NET 10, React 19 and Next.js. I've optimized queries on 10M+ record databases and led cross-functional teams at companies of all sizes.",
        "In 2024 (and 2025) I was named Progress Telerik Champion — the first in Latin America. I'm the official reseller and principal Telerik consultant in Brazil, a regular author on the official Progress blog, and delivered the first Portuguese-language Telerik webinar.",
        "I'm currently pursuing a postgraduate degree in Neuroscience, Human Development and Mediation. Understanding how the brain processes information helps me design more intuitive interfaces. Technology without empathy is just code.",
      ],
      quote: "\"Technology without empathy is just code.\"", quoteBy: "— Jefferson S. Motta",
    },
    expertise: {
      label: "EXPERTISE", title: "Stack & Skills",
      cards: [
        { i: "⚙️", t: "Backend .NET", d: "C#, .NET 10, ASP.NET Core, Web API, custom ORM (no EF Core / Dapper), xUnit, Windows Services." },
        { i: "⚛️", t: "Modern Frontend", d: "React 19, Next.js, TypeScript, Redux, Angular, Blazor. Telerik Kendo UI for enterprise components." },
        { i: "🗄️", t: "Databases", d: "SQL Server, PostgreSQL, MySQL. Advanced T-SQL, tuning, migration and multi-tenant architecture." },
        { i: "📈", t: "Observability", d: "Prometheus, Grafana, Loki, Tempo, OpenTelemetry. P50/P95/P99 metrics, Health Checks, SLOs." },
        { i: "🛡️", t: "Resilience", d: "Polly — Circuit Breaker, Retry, Bulkhead, Rate Limiting, Geo-Blocking. Zero downtime architecture." },
        { i: "☁️", t: "DevOps & Cloud", d: "Azure, Azure DevOps, GitHub Actions, IIS, Windows Server, SonarCloud." },
      ],
    },
    telerik: {
      label: "PROGRESS TELERIK", title: "Champion & Official Reseller",
      p1: "I've been using the Telerik platform since 2014 across all my projects. Over 1,250 UI components for .NET and JavaScript that let me deliver professional interfaces in a fraction of the time.",
      p2: "NASA uses Telerik DevCraft to organize data from Mars missions. Microsoft, IBM, Samsung and 450+ Fortune 500 companies rely on the same technology.",
      items: ["First Telerik Champion in Latin America", "Author on the official Progress blog", "First Portuguese-language Telerik webinar", "In-person certified training", "Reseller and principal consultant in Brazil"],
    },
    sg: {
      label: "SOURCE GENESYS", title: "SaaS Platform Generator",
      p1: "My greatest creation. Source Genesys generates complete, production-ready SaaS platforms from your database definition. Generated source code is 100% yours — no vendor lock-in.",
      p2: "Stack: .NET 10 backend, React + Next.js + Telerik Kendo UI frontend, native multi-tenant with schema isolation, enterprise observability (Prometheus, Grafana, Loki, Tempo), Polly resilience and automated testing with xUnit and Jest.",
      features: ["Automatically generated RESTful APIs", "Multi-tenant with URI/schema isolation", "React + Next.js + Kendo UI frontend", "Integrated enterprise observability", "Legacy migration: VB6, Delphi, WebForms", "Custom ORM — full control"],
      cta: "sourcegenesys.com.br",
    },
    writing: {
      label: "WRITING", title: "Articles & Book",
      items: [
        { emoji: "📝", t: "Progress Telerik Blog", d: "Regular articles on .NET, React, AI, software architecture and development best practices.", href: "https://www.telerik.com/blogs/author/jefferson-s-motta" },
        { emoji: "🏆", t: "C# Corner", d: "Over 900K views and 4 MVP awards. Technical content for the global .NET community.", href: "https://www.c-sharpcorner.com/members/jefferson-s.-motta" },
        { emoji: "📚", t: "Book on Amazon", d: "Published author — book available on Amazon covering software development topics.", href: "https://www.amazon.com/dp/B0FCBY14DJ/" },
      ],
    },
    contact: { label: "CONTACT", title: "Let's talk?", sub: "Need Telerik consulting, want to build a SaaS platform with Source Genesys or migrate a legacy system? Get in touch." },
    footer: `© 1996-${new Date().getFullYear()} Jefferson S. Motta`,
  },
};

// ═══════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════
function detectLang() {
  try { const s = localStorage.getItem("jsmotta_lang"); if (s === "pt" || s === "en") return s; } catch {}
  return (navigator.language || "en").toLowerCase().startsWith("pt") ? "pt" : "en";
}
function detectTheme() {
  try { const s = localStorage.getItem("jsmotta_theme"); if (s === "light" || s === "dark") return s; } catch {}
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function FadeIn({ children, style: extra, as: Tag = "div", ...rest }) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setV(true); obs.disconnect(); } }, { threshold: .1, rootMargin: "0px 0px -30px 0px" });
    obs.observe(el); return () => obs.disconnect();
  }, []);
  return <Tag ref={ref} style={{ opacity: v ? 1 : 0, transform: v ? "none" : "translateY(20px)", transition: "opacity .55s ease, transform .55s ease", ...extra }} {...rest}>{children}</Tag>;
}

// ═══════════════════════════════════════════════════════
// Main
// ═══════════════════════════════════════════════════════
export default function Portfolio() {
  const [lang, setLang] = useState("en");
  const [mode, setMode] = useState("light");
  const t = dict[lang];
  const c = themes[mode];

  useEffect(() => { setLang(detectLang()); setMode(detectTheme()); }, []);

  const swLang = useCallback(l => { setLang(l); try { localStorage.setItem("jsmotta_lang", l); } catch {} }, []);
  const swMode = useCallback(() => {
    const next = mode === "dark" ? "light" : "dark";
    setMode(next); try { localStorage.setItem("jsmotta_theme", next); } catch {};
  }, [mode]);

  const go = id => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const DMIcon = mode === "dark" ? sunIcon : moonIcon;

  return (
    <ThemeCtx.Provider value={c}>
      <div style={{ background: c.bg, color: c.text, fontFamily: "'DM Sans',system-ui,sans-serif", lineHeight: 1.6, minHeight: "100vh", overflowX: "hidden", transition: "background .35s, color .35s" }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=Playfair+Display:wght@600;700;800&display=swap" rel="stylesheet" />

        {/* NAV */}
        <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, background: c.navBg, backdropFilter: "blur(18px)", borderBottom: `1px solid ${c.navBrd}`, padding: "13px 0", transition: "background .35s, border-color .35s" }}>
          <Wrap style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span onClick={() => go("top")} style={{ cursor: "pointer", fontWeight: 700, fontSize: 17, letterSpacing: "-.02em", userSelect: "none" }}>
              <span style={{ color: c.acc }}>JS</span><span style={{ color: c.pri }}>Motta</span>
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 18 }}>
                {t.navItems.map((x, i) => <NavLink key={i} onClick={() => go(t.navKeys[i])}>{x}</NavLink>)}
              </div>
              {/* Theme toggle */}
              <button onClick={swMode} aria-label="Toggle theme" style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${c.toggleBrd}`, background: "transparent", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", transition: "all .25s" }}>
                <svg viewBox={DMIcon.viewBox} width={18} height={18} aria-hidden="true" focusable="false" style={{ display: "block", color: mode === "dark" ? c.pri : c.toggleInact, transition: "color .2s" }} dangerouslySetInnerHTML={{ __html: DMIcon.content }} />
              </button>
              {/* Lang toggle */}
              <div style={{ display: "flex", borderRadius: 7, overflow: "hidden", border: `1px solid ${c.toggleBrd}` }}>
                {["pt", "en"].map(l => (
                  <button key={l} onClick={() => swLang(l)} style={{ padding: "5px 11px", fontSize: 12, fontWeight: 700, fontFamily: "inherit", border: "none", cursor: "pointer", background: lang === l ? c.pri : "transparent", color: lang === l ? (mode === "dark" ? "#0a0e14" : "#fff") : c.toggleInact, transition: "all .2s" }}>
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </Wrap>
        </nav>

        {/* HERO */}
        <header id="top" style={{ padding: "148px 0 70px", position: "relative" }}>
          <div style={{ position: "absolute", top: -100, right: -180, width: 600, height: 600, background: `radial-gradient(circle,${c.grad1} 0%,transparent 65%)`, pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: -60, left: -120, width: 450, height: 450, background: `radial-gradient(circle,${c.grad2} 0%,transparent 65%)`, pointerEvents: "none" }} />
          <Wrap style={{ display: "flex", alignItems: "center", gap: 48, flexWrap: "wrap" }}>
            <div style={{ width: 190, height: 190, borderRadius: "50%", border: `3px solid ${c.priBrd}`, overflow: "hidden", flexShrink: 0, boxShadow: c.photoShadow }}>
              <img src={PHOTO_URL} alt="Jefferson S. Motta" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 15%" }} />
            </div>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: c.priSoft, border: `1px solid ${c.priBrd}`, borderRadius: 100, padding: "5px 14px", fontSize: 12, fontWeight: 600, color: c.pri, marginBottom: 18 }}>
                <span style={{ width: 6, height: 6, background: c.pri, borderRadius: "50%", display: "inline-block" }} />
                {t.hero.badge}
              </div>
              <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(2rem,4.5vw,3.1rem)", fontWeight: 800, lineHeight: 1.08, letterSpacing: "-.03em", margin: "0 0 6px" }}>{t.hero.name}</h1>
              <p style={{ fontSize: 18, color: c.acc, fontWeight: 600, margin: "0 0 14px" }}>{t.hero.role}</p>
              <p style={{ fontSize: 15, color: c.sec, maxWidth: 540, margin: "0 0 26px", lineHeight: 1.65 }}>{t.hero.sub}</p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                 <button onClick={() => go("contact")} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "12px 22px", borderRadius: 9, fontSize: 14, fontWeight: 600, textDecoration: "none", border: `1px solid ${c.accBrd}`, color: c.acc, background: "transparent", cursor: "pointer", fontFamily: "inherit" }}>{t.hero.cta2}</button>
              </div>
            </div>
          </Wrap>
        </header>

        {/* STATS */}
        <Wrap><FadeIn>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(145px,1fr))", gap: 1, background: c.priBrd, border: `1px solid ${c.priBrd}`, borderRadius: 14, overflow: "hidden", marginBottom: 60 }}>
            {t.stats.map((s, i) => (
              <div key={i} style={{ background: c.card, padding: "22px 16px", textAlign: "center", transition: "background .35s" }}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 25, fontWeight: 700, color: c.pri }}>{s.v}</div>
                <div style={{ fontSize: 11, color: c.mut, fontWeight: 500, marginTop: 2 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </FadeIn></Wrap>

        {/* ABOUT */}
        <Sect id="about" label={t.about.label} title={t.about.title}>
          {t.about.blocks.map((p, i) => <FadeIn key={i}><p style={{ fontSize: 15, color: c.sec, lineHeight: 1.72, maxWidth: 680, marginBottom: 16 }}>{p}</p></FadeIn>)}
          <FadeIn>
            <div style={{ borderLeft: `3px solid ${c.acc}`, paddingLeft: 18, marginTop: 24, background: c.quoteBg, borderRadius: "0 10px 10px 0", padding: "16px 20px 16px 18px" }}>
              <p style={{ fontSize: 16, fontStyle: "italic", color: c.text, fontWeight: 500, margin: 0 }}>{t.about.quote}</p>
              <p style={{ fontSize: 13, color: c.mut, marginTop: 6, marginBottom: 0 }}>{t.about.quoteBy}</p>
            </div>
          </FadeIn>
        </Sect>

        {/* EXPERTISE */}
        <Sect id="expertise" label={t.expertise.label} title={t.expertise.title}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(275px,1fr))", gap: 14 }}>
            {t.expertise.cards.map((x, i) => <FadeIn key={i}><SkillCard icon={x.i} title={x.t} desc={x.d} /></FadeIn>)}
          </div>
        </Sect>

        <GlowLine />

        {/* TELERIK */}
        <Sect id="telerik" label={t.telerik.label} title={t.telerik.title} bg={c.sectBg1}>
          <FadeIn><p style={{ fontSize: 15, color: c.sec, lineHeight: 1.72, maxWidth: 680, marginBottom: 14 }}>{t.telerik.p1}</p></FadeIn>
          <FadeIn><p style={{ fontSize: 15, color: c.sec, lineHeight: 1.72, maxWidth: 680, marginBottom: 26 }}>{t.telerik.p2}</p></FadeIn>
          <FadeIn>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {t.telerik.items.map((x, i) => <span key={i} style={{ background: c.priSoft, border: `1px solid ${c.priBrd}`, borderRadius: 8, padding: "7px 15px", fontSize: 13, fontWeight: 600, color: c.pri }}>{x}</span>)}
            </div>
          </FadeIn>
          <FadeIn style={{ marginTop: 22 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {["NASA", "Microsoft", "IBM", "Samsung", "450+ Fortune 500"].map((x, i) => <span key={i} style={{ background: c.card, border: `1px solid ${c.priBrd}`, borderRadius: 100, padding: "7px 18px", fontSize: 13, fontWeight: 500, color: c.sec }}>{x}</span>)}
            </div>
          </FadeIn>
        </Sect>

        <GlowLine />

        {/* SOURCE GENESYS */}
        <Sect id="sg" label={t.sg.label} title={t.sg.title} bg={c.sectBg2}>
          <FadeIn><p style={{ fontSize: 15, color: c.sec, lineHeight: 1.72, maxWidth: 680, marginBottom: 14 }}>{t.sg.p1}</p></FadeIn>
          <FadeIn><p style={{ fontSize: 15, color: c.sec, lineHeight: 1.72, maxWidth: 680, marginBottom: 26 }}>{t.sg.p2}</p></FadeIn>
          <FadeIn>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12 }}>
              {t.sg.features.map((f, i) => (
                <div key={i} style={{ background: c.card, border: `1px solid ${c.accBrd}`, borderRadius: 10, padding: "14px 18px", fontSize: 13, color: c.sec, display: "flex", alignItems: "center", gap: 10, transition: "background .35s" }}>
                  <span style={{ color: c.acc, fontWeight: 700, fontSize: 15 }}>→</span>{f}
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn style={{ marginTop: 26 }}>
            <a href="https://sourcegenesys.com.br" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 22px", borderRadius: 9, fontSize: 14, fontWeight: 600, textDecoration: "none", border: `1px solid ${c.accBrd}`, color: c.acc }}>{t.sg.cta} →</a>
          </FadeIn>
        </Sect>

        <GlowLine />

        {/* WRITING */}
        <Sect id="writing" label={t.writing.label} title={t.writing.title}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(270px,1fr))", gap: 14 }}>
            {t.writing.items.map((w, i) => <FadeIn key={i}><ExtCard {...w} /></FadeIn>)}
          </div>
        </Sect>

        {/* CONTACT */}
        <section id="contact" style={{ padding: "66px 0 44px" }}>
          <Wrap><FadeIn>
            <div style={{ background: c.card, border: `1px solid ${c.priBrd}`, borderRadius: 18, padding: "44px 36px", position: "relative", overflow: "hidden", textAlign: "center", maxWidth: 600, margin: "0 auto", boxShadow: c.shadow, transition: "background .35s" }}>
              <div style={{ position: "absolute", top: -1, left: 50, right: 50, height: 3, background: `linear-gradient(90deg,${c.pri},${c.acc})`, borderRadius: 3 }} />
              <Label text={t.contact.label} />
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(1.5rem,3vw,2.2rem)", fontWeight: 700, margin: "0 0 12px", letterSpacing: "-.02em" }}>{t.contact.title}</h2>
              <p style={{ fontSize: 15, color: c.sec, lineHeight: 1.65, marginBottom: 30, maxWidth: 460, marginLeft: "auto", marginRight: "auto" }}>{t.contact.sub}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center", marginBottom: 26 }}>
                <CRow icon="📧" text="motta@menphis.com.br" href="mailto:motta@menphis.com.br" />
                <CRow icon="📱" text="+55 51 99016-0432" href="https://wa.me/5551990160432" />
                <CRow icon="📍" text="Porto Alegre, RS — Brasil" />
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                {[["LinkedIn", "https://www.linkedin.com/in/jefferson-s-motta-lnk/"], ["GitHub", "https://github.com/jsmotta"], ["Menphis", "https://menphis.com.br"], ["Source Genesys", "https://sourcegenesys.com.br"]].map(([l, h]) => <SLink key={l} href={h}>{l}</SLink>)}
              </div>
            </div>
          </FadeIn></Wrap>
        </section>

        {/* FOOTER */}
        <footer style={{ borderTop: `1px solid ${c.priBrd}`, padding: "24px 0", textAlign: "center", transition: "border-color .35s" }}>
          <p style={{ fontSize: 12, color: c.mut, margin: 0 }}>{t.footer}</p>
        </footer>
      </div>
    </ThemeCtx.Provider>
  );
}

// ═══════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════
function Wrap({ children, style: s }) { return <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px", ...s }}>{children}</div>; }

function Label({ text }) {
  const c = useTheme();
  return <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", color: c.acc, marginBottom: 10 }}>
    <span style={{ width: 18, height: 2, background: c.acc, display: "inline-block" }} />{text}
  </div>;
}

function NavLink({ children, onClick }) {
  const c = useTheme();
  const [h, setH] = useState(false);
  return <span onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ color: h ? c.pri : c.sec, fontSize: 13, fontWeight: 500, cursor: "pointer", transition: "color .2s", userSelect: "none" }}>{children}</span>;
}

function Sect({ id, label, title, children, bg }) {
  const c = useTheme();
  return <section id={id} style={{ padding: "66px 0", background: bg || "transparent", transition: "background .35s" }}>
    <Wrap>
      <FadeIn><Label text={label} /><h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(1.5rem,3.2vw,2.3rem)", fontWeight: 700, lineHeight: 1.12, letterSpacing: "-.02em", margin: "0 0 26px", color: c.text }}>{title}</h2></FadeIn>
      {children}
    </Wrap>
  </section>;
}

function SkillCard({ icon, title, desc }) {
  const c = useTheme();
  const [h, setH] = useState(false);
  return <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ background: c.card, border: `1px solid ${h ? c.chipHov : c.priBrd}`, borderRadius: 13, padding: "24px 22px", transition: "all .3s", transform: h ? "translateY(-3px)" : "none", boxShadow: h ? c.shadow : "none" }}>
    <div style={{ fontSize: 22, marginBottom: 12 }}>{icon}</div>
    <h3 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 7px", color: c.text }}>{title}</h3>
    <p style={{ fontSize: 13, color: c.sec, lineHeight: 1.6, margin: 0 }}>{desc}</p>
  </div>;
}

function ExtCard({ emoji, t, d, href }) {
  const c = useTheme();
  const [h, setH] = useState(false);
  return <a href={href} target="_blank" rel="noopener noreferrer" onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ display: "block", textDecoration: "none", background: c.card, border: `1px solid ${h ? c.pri : c.priBrd}`, borderRadius: 13, padding: "24px 22px", transition: "all .3s", transform: h ? "translateY(-3px)" : "none", boxShadow: h ? c.shadow : "none" }}>
    <div style={{ fontSize: 24, marginBottom: 10 }}>{emoji}</div>
    <h3 style={{ fontSize: 15, fontWeight: 600, margin: "0 0 7px", color: c.text }}>{t}</h3>
    <p style={{ fontSize: 13, color: c.sec, lineHeight: 1.6, margin: 0 }}>{d}</p>
  </a>;
}

function CRow({ icon, text, href }) {
  const c = useTheme();
  const inner = <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: c.sec }}><span style={{ fontSize: 17 }}>{icon}</span>{text}</div>;
  return href ? <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", color: "inherit" }}>{inner}</a> : inner;
}

function SLink({ href, children }) {
  const c = useTheme();
  const [h, setH] = useState(false);
  return <a href={href} target="_blank" rel="noopener noreferrer" onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{ padding: "7px 16px", borderRadius: 7, fontSize: 13, fontWeight: 600, textDecoration: "none", border: `1px solid ${h ? c.pri : c.toggleBrd}`, color: h ? c.pri : c.sec, transition: "all .25s" }}>{children}</a>;
}

function GlowLine() {
  const c = useTheme();
  return <div style={{ height: 1, background: `linear-gradient(90deg,transparent,${c.pri},${c.acc},transparent)`, opacity: .18 }} />;
}
